import { useEffect, useState } from 'react'

/**
 * A clock that ticks.
 *
 * The focus list decides what is callable by comparing `next_contact_at` against
 * now, and that comparison used to sit inside a `useMemo` with `Date.now()` and
 * no timer — so once the page was open, "now" never moved. A callback promised
 * for 14:00 stayed filtered out after 14:00 until something unrelated triggered a
 * refetch, which is what made same-day callbacks feel broken rather than merely
 * late.
 *
 * Ticks only while the tab is visible: a background tab has nobody looking at it,
 * and waking every 30s to recompute a 6000-lead list would be pure waste.
 */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    let timer: number | undefined
    const tick = () => setNow(Date.now())
    const start = () => {
      stop()
      tick()
      timer = window.setInterval(tick, intervalMs)
    }
    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer)
      timer = undefined
    }
    const onVisibility = () => (document.visibilityState === 'visible' ? start() : stop())

    onVisibility()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [intervalMs])

  return now
}
